/**
 * Maps the live Automaid `control_type` vocabulary (and the vendor spec's) onto the canonical
 * set the renderer understands. Unknown values degrade to a text input — ugly beats broken.
 */
export type CanonicalControlType =
  | 'static'
  | 'text'
  | 'textarea'
  | 'select'
  | 'multiselect'
  | 'checkbox'
  | 'radio'
  | 'date'
  | 'datetime'
  | 'number';

const LIVE_TO_CANONICAL: Record<string, CanonicalControlType> = {
  // observed live (fixtures + vendor widget)
  plain_text: 'static',
  single_line: 'text',
  multi_line: 'textarea',
  drop_down: 'select',
  checkboxes: 'multiselect',
  radio_buttons: 'radio',
  checkbox: 'checkbox',
  date: 'date',
  datetime: 'datetime',
  // vendor spec enum (not yet observed live)
  text: 'text',
  textarea: 'textarea',
  select: 'select',
  multiselect: 'multiselect',
  radio: 'radio',
  number: 'number',
};

export interface NormalizedControlType {
  type: CanonicalControlType;
  raw: string;
  /** false when we fell back to a text input for an unrecognised value */
  known: boolean;
}

export function normalizeControlType(raw: string | null | undefined): NormalizedControlType {
  const key = (raw ?? '').trim().toLowerCase();
  const type = LIVE_TO_CANONICAL[key];
  if (type) return { type, raw: key, known: true };
  return { type: 'text', raw: key, known: false };
}

/** How a canonical type is serialised for `custom_fields` on booking creation. */
export type SubmissionShape = 'none' | 'value' | 'values';

export function submissionShape(type: CanonicalControlType): SubmissionShape {
  switch (type) {
    case 'static':
      return 'none';
    case 'select':
    case 'multiselect':
    case 'radio':
      return 'values';
    default:
      return 'value';
  }
}

export function hasOptions(type: CanonicalControlType): boolean {
  return submissionShape(type) === 'values';
}
